/*
 * Obermaier — product-card.js
 * Variant previews (swap image, link and screen-reader label), "+n" disclosure, hover-image
 * readiness and the hand-off to the cross-document view transition.
 *
 * Everything is delegated from document, so cards that arrive later (filters, pagination,
 * recommendations, theme editor) work without re-initialisation. Importing this module once
 * anywhere on a page is enough: import '@theme/product-card';
 */
import { announce, emit } from '@theme/core';

const CARD = '[data-product-card]';
const SWATCH = '.om-swatch[data-variant-id]';
const MORE = '.om-swatches__more';
const LINK = '[data-card-link]';

const modified = (event) => event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

/* ------------------------------------------------------------ image sources */
// Previews carry one CDN URL with the width token "width=3333" (data-image); the large image's
// src/srcset are derived from it. Keep WIDTHS in sync with `widths` in snippets/product-card.liquid.
const TOKEN = 'width=3333';
const WIDTHS = [300, 400, 500, 600, 700, 800, 1000, 1200];

function sourcesOf(swatch) {
  const base = swatch.dataset.image;
  if (!base) return { src: '', srcset: '' };
  return {
    src: base.replace(TOKEN, 'width=600'),
    srcset: WIDTHS.map((w) => `${base.replace(TOKEN, `width=${w}`)} ${w}w`).join(', '),
  };
}

/* --------------------------------------------------------------- preloading */
// One decoded probe per preview: the swap happens only once the new image is ready,
// so the tile never flashes empty. Hover and focus start the work early.
const warmed = new WeakMap();

function warm(swatch, sizes) {
  let ready = warmed.get(swatch);
  if (ready) return ready;
  const { src, srcset } = sourcesOf(swatch);
  if (!src) return Promise.resolve();
  const probe = new Image();
  probe.decoding = 'async';
  if (sizes) probe.sizes = sizes;
  if (srcset) probe.srcset = srcset;
  probe.src = src;
  const decoded = probe.decode ? probe.decode().catch(() => {}) : Promise.resolve();
  ready = Promise.race([decoded, new Promise((resolve) => setTimeout(resolve, 1500))]);
  warmed.set(swatch, ready);
  return ready;
}

function warmFrom(event) {
  const swatch = event.target instanceof Element ? event.target.closest(SWATCH) : null;
  if (!swatch || warmed.has(swatch)) return;
  const image = swatch.closest(CARD)?.querySelector('[data-card-image]');
  warm(swatch, image?.sizes);
}

/* ---------------------------------------------------------------- selection */
const tickets = new WeakMap();

export async function selectPreview(swatch) {
  const card = swatch.closest(CARD);
  if (!card) return;
  const group = swatch.closest('.om-swatches');
  const image = card.querySelector('[data-card-image]');
  const link = card.querySelector(LINK);
  const label = card.querySelector('[data-card-label]');
  const sale = card.querySelector('[data-card-sale]');

  // State and text update immediately; only the pixels wait for the decoded image.
  group?.querySelectorAll('.om-swatch[aria-current]').forEach((el) => el.removeAttribute('aria-current'));
  swatch.setAttribute('aria-current', 'true');
  card.dataset.selected = swatch.dataset.variantId;
  if (link) link.href = swatch.href;
  if (label && swatch.dataset.label) label.textContent = swatch.dataset.label;
  if (sale) {
    const text = swatch.dataset.sale || '';
    sale.textContent = text;
    sale.hidden = !text;
  }
  announce(swatch.dataset.label || swatch.getAttribute('aria-label') || '');
  emit('om:card:select', { productId: Number(card.dataset.productId), variantId: Number(swatch.dataset.variantId) });

  const { src, srcset } = sourcesOf(swatch);
  if (!image || !src || image.dataset.variantId === swatch.dataset.variantId) return;
  const ticket = (tickets.get(card) || 0) + 1;
  tickets.set(card, ticket);
  await warm(swatch, image.sizes);
  if (tickets.get(card) !== ticket) return; // a newer choice won

  image.dataset.variantId = swatch.dataset.variantId;
  image.srcset = srcset;
  image.src = src;
  image.classList.remove('is-swapped');
  void image.offsetWidth; // restart the fade
  image.classList.add('is-swapped');
}

/* --------------------------------------------------------------- disclosure */
const isShown = (el) => (el.checkVisibility ? el.checkVisibility() : el.offsetParent !== null);

function toggle(button) {
  const group = button.closest('.om-swatches');
  if (!group) return;
  const open = !group.classList.contains('is-open');
  const firstHidden = open ? [...group.querySelectorAll('.om-swatch')].find((el) => !isShown(el)) : null;
  group.classList.toggle('is-open', open);
  button.setAttribute('aria-expanded', String(open));
  // Continue where the new content starts; collapsing keeps focus on the button.
  if (firstHidden) firstHidden.focus({ preventScroll: true });
}

/* ------------------------------------------------------- view transitions */
// The product page names its main image the same way, so the tile glides into the gallery.
// Exactly one element per page may carry the name.
function clearTransitionNames() {
  document.querySelectorAll('[data-vt-named]').forEach((el) => {
    el.style.viewTransitionName = '';
    delete el.dataset.vtNamed;
  });
}

function nameForTransition(link) {
  const card = link.closest(CARD);
  if (!card?.dataset.productId) return;
  clearTransitionNames();
  link.style.viewTransitionName = `om-product-${card.dataset.productId}`;
  link.dataset.vtNamed = '';
}

/* -------------------------------------------------------------- hover image */
function markReady(img) {
  const layer = img.closest('.om-card__alt');
  if (!layer) return;
  const src = img.currentSrc || img.src;
  if (img.complete && img.naturalWidth > 1 && !src.startsWith('data:')) layer.classList.add('is-ready');
}

/* ------------------------------------------------------------------ wiring */
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || !(event.target instanceof Element)) return;

  const swatch = event.target.closest(SWATCH);
  if (swatch) {
    if (modified(event)) return; // new tab / window keeps the variant link
    event.preventDefault();
    selectPreview(swatch);
    return;
  }

  const more = event.target.closest(MORE);
  if (more) {
    toggle(more);
    return;
  }

  const link = event.target.closest(LINK);
  if (link && !modified(event)) nameForTransition(link);
});

document.addEventListener('pointerover', warmFrom, { passive: true });
document.addEventListener('focusin', warmFrom);

// Load events do not bubble; capture them for hover layers, including cards added later.
document.addEventListener(
  'load',
  (event) => {
    if (event.target instanceof HTMLImageElement && event.target.parentElement?.closest('.om-card__alt')) markReady(event.target);
  },
  true
);
document.querySelectorAll('.om-card__alt img').forEach(markReady);

// Back/forward cache restores the page as it was left: drop the name set on the way out.
addEventListener('pageshow', (event) => {
  if (event.persisted) clearTransitionNames();
});
